import { useState, useEffect } from 'react'
import type { Milestone } from '../types'
import { api } from '../api'

const FASES = [
  { id: 'preparacao', nome: 'Preparacao', cor: '#8b5cf6' },
  { id: 'lancamento', nome: 'Lancamento', cor: '#3b82f6' },
  { id: 'crescimento', nome: 'Crescimento', cor: '#10b981' },
  { id: 'consolidacao', nome: 'Consolidacao', cor: '#f59e0b' },
]

const STATUS_CORES: Record<string, string> = {
  pendente: '#9ca3af',
  em_andamento: '#3b82f6',
  concluido: '#22c55e',
  atrasado: '#ef4444',
  cancelado: '#6b7280',
}

export default function RoadmapPage() {
  const [milestones, setMilestones] = useState<Milestone[]>([])
  const [showForm, setShowForm] = useState(false)
  const [selected, setSelected] = useState<Milestone | null>(null)
  const [loading, setLoading] = useState(true)

  const load = () => api.getMilestones().then((m) => { setMilestones(m); setLoading(false) })
  useEffect(() => { load() }, [])

  if (loading) return <div className="flex items-center justify-center h-full text-gray-400">Carregando...</div>

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Roadmap de Expansao</h1>
          <p className="text-sm text-gray-500">{milestones.length} marco(s)</p>
        </div>
        <button onClick={() => { setSelected(null); setShowForm(true) }}
          className="text-sm bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
          + Novo Marco
        </button>
      </div>

      {/* Timeline por fase */}
      <div className="space-y-8">
        {FASES.map((fase) => {
          const faseMilestones = milestones.filter((m) => m.fase === fase.id)
          if (faseMilestones.length === 0 && milestones.length > 0) return null
          return (
            <div key={fase.id}>
              <div className="flex items-center gap-2 mb-3">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: fase.cor }} />
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{fase.nome}</h2>
              </div>
              <div className="space-y-3 ml-5 border-l-2 pl-4" style={{ borderColor: fase.cor + '40' }}>
                {faseMilestones.map((m) => (
                  <div key={m.id} onClick={() => setSelected(m)}
                    className="bg-white rounded-lg shadow-sm border p-4 cursor-pointer hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium text-gray-800">{m.titulo}</h3>
                      <span className="text-xs px-2 py-0.5 rounded-full text-white"
                        style={{ backgroundColor: STATUS_CORES[m.status] }}>
                        {m.status.replace('_', ' ')}
                      </span>
                    </div>
                    {m.descricao && <p className="text-sm text-gray-600 mb-2">{m.descricao}</p>}
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <div className="bg-gray-100 rounded-full h-2 overflow-hidden">
                          <div className="h-full rounded-full transition-all"
                            style={{ width: `${m.progresso}%`, backgroundColor: STATUS_CORES[m.status] }} />
                        </div>
                      </div>
                      <span className="text-xs text-gray-500">{m.progresso}%</span>
                      <span className="text-xs text-gray-400">
                        Alvo: {new Date(m.data_alvo + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                  </div>
                ))}
                {faseMilestones.length === 0 && (
                  <p className="text-sm text-gray-400 italic">Nenhum marco nesta fase</p>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Detail */}
      {selected && !showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-12 overflow-auto" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 mb-12" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">{selected.titulo}</h2>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>
            <div className="p-6 space-y-4">
              {selected.descricao && <p className="text-sm text-gray-700">{selected.descricao}</p>}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs text-gray-500">Fase</p>
                  <p className="font-medium">{FASES.find((f) => f.id === selected.fase)?.nome}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Status</p>
                  <p className="font-medium">{selected.status.replace('_', ' ')}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Data Alvo</p>
                  <p className="font-medium">{new Date(selected.data_alvo + 'T00:00:00').toLocaleDateString('pt-BR')}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Progresso</p>
                  <p className="font-medium">{selected.progresso}%</p>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <button onClick={() => setShowForm(true)} className="text-sm px-4 py-2 border rounded-lg hover:bg-gray-100">Editar</button>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      {showForm && (
        <MilestoneForm
          milestone={selected}
          onClose={() => { setShowForm(false); setSelected(null) }}
          onSaved={() => { setShowForm(false); setSelected(null); load() }}
        />
      )}
    </div>
  )
}

function MilestoneForm({ milestone, onClose, onSaved }: {
  milestone: Milestone | null
  onClose: () => void
  onSaved: () => void
}) {
  const [titulo, setTitulo] = useState(milestone?.titulo ?? '')
  const [descricao, setDescricao] = useState(milestone?.descricao ?? '')
  const [fase, setFase] = useState(milestone?.fase ?? 'preparacao')
  const [dataAlvo, setDataAlvo] = useState(milestone?.data_alvo ?? '')
  const [dataInicio, setDataInicio] = useState(milestone?.data_inicio ?? '')
  const [status, setStatus] = useState(milestone?.status ?? 'pendente')
  const [progresso, setProgresso] = useState(milestone?.progresso ?? 0)
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    if (!titulo.trim() || !dataAlvo) return
    setSaving(true)
    try {
      const data = {
        titulo: titulo.trim(),
        descricao: descricao.trim() || undefined,
        fase,
        data_alvo: dataAlvo,
        data_inicio: dataInicio || undefined,
        status,
        progresso,
      }
      if (milestone) {
        await api.updateMilestone(milestone.id, data)
      } else {
        await api.createMilestone(data)
      }
      onSaved()
    } finally { setSaving(false) }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-12 overflow-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 mb-12" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold">{milestone ? 'Editar Marco' : 'Novo Marco'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        <div className="p-6 space-y-4">
          <input value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Titulo do marco..."
            className="w-full text-lg font-medium border-b pb-2 focus:outline-none focus:border-blue-500" />
          <textarea value={descricao} onChange={(e) => setDescricao(e.target.value)} placeholder="Descricao..." rows={2}
            className="w-full text-sm border rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Fase</label>
              <select value={fase} onChange={(e) => setFase(e.target.value)} className="w-full text-sm border rounded-lg p-2">
                {FASES.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value as Milestone['status'])} className="w-full text-sm border rounded-lg p-2">
                <option value="pendente">Pendente</option>
                <option value="em_andamento">Em andamento</option>
                <option value="concluido">Concluido</option>
                <option value="atrasado">Atrasado</option>
                <option value="cancelado">Cancelado</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Data Inicio</label>
              <input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} className="w-full text-sm border rounded-lg p-2" />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Data Alvo</label>
              <input type="date" value={dataAlvo} onChange={(e) => setDataAlvo(e.target.value)} className="w-full text-sm border rounded-lg p-2" />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Progresso: {progresso}%</label>
            <input type="range" min={0} max={100} value={progresso} onChange={(e) => setProgresso(Number(e.target.value))}
              className="w-full" />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
          <button onClick={onClose} className="text-sm px-4 py-2 border rounded-lg hover:bg-gray-100">Cancelar</button>
          <button onClick={handleSave} disabled={saving || !titulo.trim() || !dataAlvo}
            className="text-sm px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Salvando...' : milestone ? 'Salvar' : 'Criar'}
          </button>
        </div>
      </div>
    </div>
  )
}
